// Content.tsx - Fixed version
import CustomTable from "@/shared/components/common/table";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid, TextField, CircularProgress } from "@mui/material"; // Added CircularProgress
import { useEffect, useState } from "react";
import LineChart from "./line.chart";
import CustomSelect from "@/shared/components/common/select";
import AdapterMomentJalali from "@/shared/components/common/datePeacker";
import { useQuery } from "@tanstack/react-query";
import { fetchListWithdrawableCreditBalance, fetchNoduedateCreditBalance } from "@/api/merchant/request.get";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import moment from "jalali-moment";
import { OutIcon } from "@/shared/components/common/icons";
import AlertDialog from "@/shared/components/kit/modal";
import ContentModal from "./contentModal";
import BoxKit from "@/shared/components/kit/box";
import ContentSkeleton from "@/modules/user/components/walletReports/content.skeleton";

const Content = ({ selectedNumber }: { selectedNumber: string }) => {
  const [value, setValue] = useState(10);
  const [dateValue, setDateValue] = useState<Date | string | null>(null);
  const tabs = ["همه", "درگاه ipg", "دستگاه pos"];
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [dataModal, setDataModal] = useState<any>();

  // FIXED: Added page to queryKey and included it in fetch function
  const { data: dataNoduedateCreditBalance, isLoading, refetch,isRefetching } = useQuery({
    queryKey: ["nodueCreditBalance-data", selectedNumber, page, value], // Added value to queryKey
    enabled: selectedNumber === "all",
    queryFn: () => fetchNoduedateCreditBalance(page,value),
  });

  useEffect(() => {
    refetch();
  }, [selectedNumber, page , value]); // Added page to dependencies

  const { data: dataWithdrawableCreditBalance, isLoading: isLoadingWithdrawable } = useQuery({
    queryKey: ["WithdrawableCreditBalanceinWallet-dataTransAction", selectedNumber,page,value],
    enabled: selectedNumber !== "all",
    queryFn: () => fetchListWithdrawableCreditBalance(page,value),
  });

  const handleChange = (val: any) => setValue(val);
  const handleChangeDate = (date: string) => setDateValue(date);
  const [activeTab, setActiveTab] = useState("همه");

  const dataHeader = selectedNumber === "all"
    ? [
      { id: "rowId", title: "ردیف" },
      { id: "credit", title: "مبلغ خرید اعتباری" },
      { id: "amount", title: "مبلغ خرید نقدی" },
      { id: "fullname", title: "نام و نام خانوادگی کاربر" },
      { id: "created_at", title: "تاریخ ثبت خرید" },
      { id: "deposit", title: "مبلغ تسویه" },
      { id: "deposit_date", title: "تاریخ تسویه" },
      { id: "gateway", title: "نوع خرید" },
      { id: "tracking_code", title: "کد رهگیری" },
      { id: "moreElement", title: "" },
    ]
    : [
      { id: "rowId", title: "ردیف" },
      { id: "deposit", title: "مبلغ تسویه(ریال) " },
      { id: "deposit_date", title: "تاریخ تسویه" },
      { id: "fullname", title: "نام و نام خانوادگی کاربر" },
      { id: "created_at", title: "تاریخ ثبت خرید" },
      { id: "gateway", title: "نوع خرید" },
      { id: "tracking_code", title: "کد رهگیری" },
      { id: "moreElement", title: "" },
    ];

  // Calculate row numbers based on page
  const calculateRowId = (index: number) => {
    const perPage = dataNoduedateCreditBalance?.per_page || 10;
    return toPersianNumber((page - 1) * perPage + index + 1);
  };

  const fakeData =
    selectedNumber === "all"
      ? dataNoduedateCreditBalance?.noDuedateCredit?.map((item, index: number) => ({
        ...item,
        rowId: calculateRowId(index), // Fixed: Use page-aware calculation
        credit: numberSeparator(item.credit),
        amount: numberSeparator(item.amount),
        tracking_code: toPersianNumber(`${item?.tracking_code}`),
        created_at: (
          <StackKit direction="row" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ width: "100%" }}>
            <TypoGraphyKit variant="body1">{toPersianNumber(item?.created_at.split(" ")[2])}</TypoGraphyKit>
            <TypoGraphyKit variant="body1">{toPersianNumber(moment(item?.created_at.split(" ")[0]).locale("fa").format("YYYY/MM/DD"))}</TypoGraphyKit>
          </StackKit>
        ),
        deposit_date: (
          <StackKit direction="row" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ width: "100%" }}>
            <span>{toPersianNumber(moment(item?.deposit_date).locale("fa").format("HH:MM"))}</span>
            <span>{toPersianNumber(moment(item?.deposit_date).locale("fa").format("YYYY/MM/DD"))}</span>
          </StackKit>
        ),
        deposit: numberSeparator(item?.deposit),
        gateway: item.gateway || "-",
        moreElement: (
          <StackKit justifyContent="center" alignItems="center">
            <StackKit sx={{ width: "20%", cursor: "pointer" }} justifyContent="center" alignItems="center" onClick={() => { setOpenDialog(true); setDataModal(item); }}>
              <OutIcon />
            </StackKit>
          </StackKit>
        ),
      }))
      : dataWithdrawableCreditBalance?.data?.withdrawableCredit?.map((item: any, index: number) => ({
        ...item,
        rowId: toPersianNumber(index + 1),
        credit: numberSeparator(item.credit),
        deposit_date: toPersianNumber(moment(item?.deposit_date).format("YYYY/MM/DD")),
        created_at: (
          <StackKit direction="row" gap={1} flexWrap="wrap" sx={{ width: "fit-content" }}>
            <TypoGraphyKit variant="caption">{toPersianNumber(item?.created_at.split(" ")[2])}</TypoGraphyKit>
            <TypoGraphyKit variant="caption">{toPersianNumber(moment(item?.created_at.split(" ")[0]).locale("fa").format("YYYY/MM/DD"))}</TypoGraphyKit>
          </StackKit>
        ),
        tracking_code: item?.tracking_code ? toPersianNumber(item?.tracking_code) : "-",
        deposit: numberSeparator(item?.deposit),
        gateway: item.gateway || "-",
        moreElement: (
          <StackKit justifyContent="center" alignItems="center">
            <StackKit sx={{ width: "20%", cursor: "pointer" }} justifyContent="center" alignItems="center" onClick={() => { setOpenDialog(true); setDataModal(item); }}>
              <OutIcon />
            </StackKit>
          </StackKit>
        ),
      }));

  return (
    <StackKit sx={{ width: "100%" }} gap={3}>
      <BoxKit sx={{ width: "100%" }}>
        <LineChart selectedNumber={selectedNumber} />
      </BoxKit>

      {isLoading || isLoadingWithdrawable ? (
        <ContentSkeleton />
      ) : (
        <Grid container spacing={2}>
          {/* Controls Row */}
          <Grid container spacing={2} justifyContent="space-between" width={'100%'}>
            {/* Left controls: Tabs + Select */}
            <Grid size={{ xs: 12, md: 6 }}>
              <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="flex-start" alignItems="center" flexWrap="wrap" gap={2} sx={{ width: "100%" }}>
                <StackKit direction="row" justifyContent="flex-start" alignItems="center" flexWrap="wrap" gap={1} sx={{ bgcolor: "#5A5A5A", padding: "10px", borderRadius: "8px" }}>
                  {tabs.map((tab) => (
                    <StackKit
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      sx={{
                        padding: "6px 16px",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        borderRadius: "8px",
                        bgcolor: activeTab === tab ? "#434343" : "transparent",
                        color: activeTab === tab ? "#fff" : "#B3B3B3",
                        border: activeTab === tab ? "1px solid #434343" : "1px solid transparent",
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: activeTab === tab ? "#434343" : "#666666" },
                        height: "38px",
                        margin: "0!important",
                      }}
                    >
                      <TypoGraphyKit variant="body2" sx={{ color: "inherit" }}>{tab}</TypoGraphyKit>
                    </StackKit>
                  ))}
                </StackKit>
                <CustomSelect label={<TypoGraphyKit variant="body1" sx={{ color: "#A4A4A4" }}>تراکنش آخر</TypoGraphyKit>} options={[5, 10, 15, 20]} onChange={handleChange} value={value || 10} />
              </StackKit>
            </Grid>

            {/* Right controls: DatePicker + Search + Button */}
            <Grid size={{ xs: 12, md: 6 }}>
              <StackKit direction={{ xs: "column", sm: "row" }} justifyContent={{ xs: "center", md: "flex-end" }} alignItems="center" flexWrap="wrap" gap={1} sx={{ width: "100%" }}>
                <AdapterMomentJalali handleChange={handleChangeDate} />
                <TextField placeholder="وارد کردن جزئیات جستجو" sx={{ width: { xs: "100%", sm: "auto" } }} />
                <CustomButton>
                  <TypoGraphyKit variant="body1">جستجو</TypoGraphyKit>
                </CustomButton>
              </StackKit>
            </Grid>
          </Grid>

          {/* Table with Loading State */}
          <Grid size={{ xs: 12 }}>
            <BoxKit sx={{ overflowX: 'auto', position: 'relative' }}>
              {/* Loading Spinner */}
              {isRefetching && (
                <BoxKit 
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    zIndex: 10,
                    borderRadius: '8px'
                  }}
                >
                  <CircularProgress sx={{ color: '#fff' }} />
                </BoxKit>
              )}
              
              <CustomTable 
                dataHeader={dataHeader} 
                dataTbody={fakeData || []} 
                total={dataNoduedateCreditBalance?.total || 0}
                itemsPerPage={value} // Changed from dataNoduedateCreditBalance?.per_page to value
                currentPage={parseInt(dataNoduedateCreditBalance?.current_page?.toString() || "1")}
                setPages={setPage}
                isLoading={isRefetching}
              />
            </BoxKit>
          </Grid>
        </Grid>
      )}

      {/* Modal */}
      <AlertDialog isHeader header={<TypoGraphyKit variant="h6">جزئیات خرید اعتباری</TypoGraphyKit>} open={openDialog} onClose={() => setOpenDialog(false)} heightMain={"450px"}>
        <ContentModal dataModal={dataModal} />
      </AlertDialog>
    </StackKit>
  );
};

export default Content;